<template>
  <div class="pce">
    <span class="pce__title">Contactos visibles para inquilinos</span>
    <p class="pce__hint">Aparecen en "Contactos del parque" dentro del portal del inquilino. Si no agregas ninguno se muestra el teléfono y correo generales del parque.</p>

    <div v-for="c in contacts" :key="c.id" class="pce__item">
      <template v-if="editingId === c.id">
        <input v-model="draft.name" class="pce__input" placeholder="Nombre" maxlength="120" />
        <input v-model="draft.role" class="pce__input" placeholder="Cargo (ej. Administración)" maxlength="120" />
        <input v-model="draft.phone" class="pce__input" placeholder="Teléfono" maxlength="40" />
        <input v-model="draft.email" class="pce__input" placeholder="Correo" maxlength="160" />
        <div class="pce__actions">
          <button class="pce__btn pce__btn--primary" :disabled="busy" @click="save(c.id)">Guardar</button>
          <button class="pce__btn" @click="cancel">Cancelar</button>
        </div>
      </template>
      <template v-else>
        <div class="pce__info">
          <span class="pce__name">{{ c.name }}</span>
          <span class="pce__meta">{{ [c.role, c.phone, c.email].filter(Boolean).join(' · ') }}</span>
        </div>
        <button class="pce__icon" title="Editar" @click="startEdit(c)"><span class="mdi mdi-pencil-outline"></span></button>
        <button class="pce__icon pce__icon--danger" title="Eliminar" @click="remove(c.id)"><span class="mdi mdi-trash-can-outline"></span></button>
      </template>
    </div>

    <div v-if="adding" class="pce__item">
      <input v-model="draft.name" class="pce__input" placeholder="Nombre" maxlength="120" />
      <input v-model="draft.role" class="pce__input" placeholder="Cargo (ej. Administración)" maxlength="120" />
      <input v-model="draft.phone" class="pce__input" placeholder="Teléfono" maxlength="40" />
      <input v-model="draft.email" class="pce__input" placeholder="Correo" maxlength="160" />
      <div class="pce__actions">
        <button class="pce__btn pce__btn--primary" :disabled="busy" @click="save(null)">Agregar</button>
        <button class="pce__btn" @click="cancel">Cancelar</button>
      </div>
    </div>

    <p v-if="error" class="pce__error">{{ error }}</p>
    <button v-if="!adding && !editingId && contacts.length < 10" class="pce__add" @click="startAdd"><span class="mdi mdi-plus"></span> Agregar contacto</button>
  </div>
</template>

<script setup>
import { onMounted, reactive, ref } from 'vue'
import { getParkContacts, createParkContact, updateParkContact, deleteParkContact } from '@/services/parkContacts'

const props = defineProps({ parkId: { type: String, required: true } })

const contacts = ref([])
const adding = ref(false)
const editingId = ref(null)
const busy = ref(false)
const error = ref('')
const draft = reactive({ name: '', role: '', phone: '', email: '' })

const ERRORS = {
  NAME_REQUIRED: 'Escribe el nombre del contacto.',
  CONTACT_DATA_REQUIRED: 'Agrega al menos un teléfono o un correo.',
  TOO_MANY_CONTACTS: 'Puedes tener hasta 10 contactos.',
}

async function load() {
  try {
    contacts.value = await getParkContacts(props.parkId)
  } catch {
    error.value = 'No se pudieron cargar los contactos.'
  }
}

function reset() {
  Object.assign(draft, { name: '', role: '', phone: '', email: '' })
  error.value = ''
}
function startAdd() {
  reset()
  adding.value = true
}
function startEdit(c) {
  reset()
  Object.assign(draft, { name: c.name, role: c.role || '', phone: c.phone || '', email: c.email || '' })
  editingId.value = c.id
}
function cancel() {
  adding.value = false
  editingId.value = null
  reset()
}

async function save(id) {
  busy.value = true
  error.value = ''
  try {
    if (id) await updateParkContact(props.parkId, id, { ...draft })
    else await createParkContact(props.parkId, { ...draft })
    cancel()
    await load()
  } catch (err) {
    error.value = ERRORS[err.message] || 'No se pudo guardar el contacto.'
  } finally {
    busy.value = false
  }
}

async function remove(id) {
  error.value = ''
  try {
    await deleteParkContact(props.parkId, id)
    await load()
  } catch {
    error.value = 'No se pudo eliminar el contacto.'
  }
}

onMounted(load)
</script>

<style scoped>
.pce { display: flex; flex-direction: column; gap: 8px; padding: 6px 0 4px; }
.pce__title { font: 500 11px Inter, sans-serif; text-transform: uppercase; letter-spacing: 0.4px; color: #94a3b8; }
.pce__hint { margin: 0; font: 400 11px/1.4 Inter, sans-serif; color: #94a3b8; }
.pce__item { display: flex; flex-wrap: wrap; align-items: center; gap: 6px; padding: 8px 10px; border: 1px solid #e2e8f0; border-radius: 6px; background: #fff; }
.pce__info { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 2px; }
.pce__name { font: 500 13px Inter, sans-serif; color: #1e3a5f; }
.pce__meta { font: 400 11px Inter, sans-serif; color: #94a3b8; word-break: break-all; }
.pce__input { flex: 1 1 45%; min-width: 120px; height: 30px; padding: 0 8px; border: 1px solid #e2e8f0; border-radius: 6px; font: 400 12px Inter, sans-serif; color: #1e3a5f; }
.pce__actions { display: flex; gap: 6px; width: 100%; }
.pce__btn { height: 28px; padding: 0 12px; border: 1px solid #e2e8f0; border-radius: 6px; background: #fff; color: #1e3a5f; font: 500 12px Inter, sans-serif; cursor: pointer; }
.pce__btn--primary { background: #3b82f6; border-color: #3b82f6; color: #fff; }
.pce__btn:disabled { opacity: 0.5; }
.pce__icon { width: 28px; height: 28px; border: none; background: none; color: #64748b; font-size: 16px; cursor: pointer; border-radius: 6px; }
.pce__icon:hover { background: #f1f5f9; }
.pce__icon--danger:hover { color: #ef4444; }
.pce__add { align-self: flex-start; height: 30px; padding: 0 12px; border: 1px dashed #94a3b8; border-radius: 6px; background: #fff; color: #64748b; font: 500 12px Inter, sans-serif; cursor: pointer; display: flex; align-items: center; gap: 4px; }
.pce__error { margin: 0; font: 400 12px Inter, sans-serif; color: #b91c1c; }
</style>